'use client';

import { useRouter } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';
import { saveEncryptedDesktopCookies } from '@/db/desktop-auth-cookies';
import { hasTauriBridge, tauriInvoke, tauriListen } from '@/lib/desktop/tauri-bridge';
import { notifyOfflineModeChanged } from '@/lib/offline-mode';
import { runtime } from '@/lib/runtime';
import { resetDesktopGraphQLClient } from '@/lib/twitter/graphql/state';

export type DesktopLoginStatus = 'idle' | 'waiting' | 'captured' | 'error';

interface EncryptedCookiePayload {
  captured_at: string;
  ciphertext: string;
  expires_at?: string | null;
  nonce: string;
}

export interface DesktopTwitterLoginOptions {
  beforeStart?: () => Promise<void> | void;
}

export function useDesktopTwitterLogin({ beforeStart }: DesktopTwitterLoginOptions = {}) {
  const router = useRouter();
  const [status, setStatus] = useState<DesktopLoginStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const listenersReference = useRef<Array<() => void>>([]);
  const cancelledReference = useRef(false);

  useEffect(() => {
    if (!runtime.isDesktop) {
      return;
    }

    cancelledReference.current = false;
    const teardown = () => {
      for (const dispose of listenersReference.current) {
        try {
          dispose();
        } catch {
          // no-op: releasing listeners should not throw
        }
      }
      listenersReference.current = [];
    };

    const start = async () => {
      if (!hasTauriBridge()) {
        setStatus('error');
        setError('Desktop bridge unavailable. Please restart Sweetistics.');
        return;
      }

      try {
        setStatus('waiting');
        setError(null);

        // Drop any cached GraphQL client so the next timeline fetch bootstraps with the new cookie payload.
        resetDesktopGraphQLClient();

        if (beforeStart) {
          await beforeStart();
        }

        try {
          globalThis.localStorage.setItem('sweetistics.offlineMode', '1');
          globalThis.sessionStorage.setItem('sweetistics.offlineMode', '1');
          notifyOfflineModeChanged();
        } catch (storageError) {
          console.warn('Failed to persist offline mode flags', storageError);
        }

        const handleCookieCapture = async (payload: EncryptedCookiePayload) => {
          if (cancelledReference.current) {
            return;
          }

          try {
            await saveEncryptedDesktopCookies({
              ciphertext: payload.ciphertext,
              nonce: payload.nonce,
              expiresAt: payload.expires_at ?? null,
              capturedAt: payload.captured_at,
            });
            // Cookie persistence succeeded – immediately transition to the timeline where
            // React Query rehydrates using the freshly captured credentials emitted above.
            setStatus('captured');
            router.replace('/timeline');
            teardown();
          } catch (persistError) {
            setStatus('error');
            setError(persistError instanceof Error ? persistError.message : 'Failed to persist encrypted cookies');
          }
        };

        const cookieHandlerUnlisten = await tauriListen<EncryptedCookiePayload>(
          'twitter-cookie-captured',
          (payload) => {
            handleCookieCapture(payload).catch((captureError: unknown) => {
              console.error('Failed to persist desktop Twitter cookies', captureError);
            });
          }
        );

        const errorUnlisten = await tauriListen<string>('twitter-cookie-watch-error', (message) => {
          if (cancelledReference.current) {
            return;
          }
          setStatus('error');
          setError(typeof message === 'string' ? message : 'Cookie watcher reported an error.');
        });

        listenersReference.current.push(cookieHandlerUnlisten, errorUnlisten);

        await tauriInvoke('start_twitter_login');
        await tauriInvoke('watch_twitter_cookies');
      } catch (invokeError) {
        if (cancelledReference.current) {
          return;
        }
        setStatus('error');
        setError(invokeError instanceof Error ? invokeError.message : 'Twitter login failed.');
        teardown();
      }
    };

    start().catch((startError: unknown) => {
      console.error('Desktop Twitter login failed to start', startError);
      setStatus('error');
      setError(startError instanceof Error ? startError.message : 'Unable to start desktop Twitter login.');
      teardown();
    });

    return () => {
      cancelledReference.current = true;
      teardown();
    };
  }, [beforeStart, router]);

  return { status, error };
}
